import React, { useEffect, useState } from 'react';
import { payrunApi, quickSuperApi } from '../api/client';
import type { PayRunResult, QuickSuperLine, QuickSuperMatch, QuickSuperMember } from '../types';

const QUICKSUPER_URL = 'https://www.clearinghouse.australiansuper.com/ClientHomeView';

interface QuickSuperStateProps {
  // After a draft pay run exists: lists what the server says was not sent to
  // Xero and allows finalising.
  result?: PayRunResult;
  // Before a pay run exists: a read-only preview of the unticked contractors.
  preview?: { lines: QuickSuperLine[]; xeroTotal: number };
  // Used to build the QuickSuper contribution file.
  importId?: number;
  members: QuickSuperMember[];
  matches: Record<string, QuickSuperMatch>;
  onChoose: (lineId: number, memberId: number | null) => void;
  periodStart: string; // YYYY-MM-DD
  periodEnd: string;
  onBack: () => void;
  onFinalised?: () => void;
}

function fmt(n: number): string {
  return n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function errorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
  return data?.error ?? fallback;
}

export default function QuickSuperState({
  result,
  preview,
  importId,
  members,
  matches,
  onChoose,
  periodStart,
  periodEnd,
  onBack,
  onFinalised,
}: QuickSuperStateProps) {
  const runId = result?.payrun.id;
  const isPreview = !result;
  const previewTotal = preview
    ? Math.round(preview.lines.reduce((sum, l) => sum + parseFloat(l.superAmount), 0) * 100) / 100
    : 0;
  const [lines, setLines] = useState<QuickSuperLine[] | null>(preview ? preview.lines : null);
  const [total, setTotal] = useState(previewTotal);
  const [finalised, setFinalised] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [finalising, setFinalising] = useState(false);
  const [finaliseError, setFinaliseError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const allMatched = !!lines && lines.length > 0 && lines.every((l) => l.id !== undefined && matches[l.id]?.memberId);
  const periodValid = /^\d{4}-\d{2}-\d{2}$/.test(periodStart) && /^\d{4}-\d{2}-\d{2}$/.test(periodEnd);
  const canDownload = allMatched && periodValid && importId !== undefined && !downloading;

  async function handleDownload() {
    if (!lines || importId === undefined) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const { blob, filename } = await quickSuperApi.downloadContributionFile({
        importId,
        lineIds: lines.map((l) => l.id!).filter((id) => id !== undefined),
        periodStart,
        periodEnd,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Could not build the file');
    } finally {
      setDownloading(false);
    }
  }

  useEffect(() => {
    if (runId === undefined) return;
    let cancelled = false;
    payrunApi.quickSuper(runId)
      .then(({ data }) => {
        if (cancelled) return;
        setLines(data.lines);
        setTotal(data.total);
        setFinalised(data.finalised);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(errorMessage(err, 'Could not load the remaining contractors.'));
      });
    return () => { cancelled = true; };
  }, [runId]);

  async function handleFinalise() {
    if (runId === undefined) return;
    setFinalising(true);
    setFinaliseError(null);
    try {
      await payrunApi.finalise(runId);
      setFinalised(true);
      onFinalised?.();
    } catch (err) {
      setFinaliseError(errorMessage(err, 'Could not finalise this pay run.'));
    } finally {
      setFinalising(false);
    }
  }

  const isFinalisedView = finalised;
  const xeroTotal = result ? (result.expectedSuperTotal ?? result.totalSuper) : (preview?.xeroTotal ?? 0);

  return (
    <div className="flex flex-col items-center py-12 px-8 max-w-lg mx-auto w-full">
      <h2 className="text-2xl font-bold text-gray-900 mb-1 text-center">
        {isPreview ? 'QuickSuper Contributions' : 'Remaining Contractors'}
      </h2>
      <p className="text-sm text-gray-500 mb-6 text-center">
        {isPreview
          ? 'These contractors are not ticked to send to Xero. Pay their super through QuickSuper.'
          : 'These contractors were not sent to Xero. Pay their super through QuickSuper.'}
      </p>
      {isPreview && (
        <div className="w-full mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm text-amber-900">
          Preview based on your current ticks. Nothing has been sent to Xero yet. Create the draft pay run, then use{' '}
          <strong>Next</strong> to finalise.
        </div>
      )}

      <div className="w-full mb-6 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-left text-sm text-sky-900">
        <p className="font-semibold mb-1">Pay via AustralianSuper QuickSuper</p>
        <p>
          These contractors can be paid in the AustralianSuper QuickSuper clearing house:{' '}
          <a href={QUICKSUPER_URL} target="_blank" rel="noopener noreferrer" className="font-medium underline">
            clearinghouse.australiansuper.com
          </a>
          . Pay each amount below, then click <strong>Finalise</strong> to archive this month's super payment run.
        </p>
      </div>

      {loadError && (
        <div className="w-full mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{loadError}</div>
      )}

      {!lines && !loadError && <p className="text-sm text-gray-500 mb-6">Loading…</p>}

      {lines && (
        <div className="w-full mb-6 rounded-xl border border-gray-200 bg-white text-left">
          {lines.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-500">
              Every contractor in this import was sent to Xero, so there is nothing to pay in QuickSuper.
            </p>
          ) : (
            lines.map((l, i) => {
              const match = l.id !== undefined ? matches[l.id] : undefined;
              const memberId = match?.memberId ?? null;
              return (
                <div key={`${l.contractorName}-${i}`} className="px-4 py-3 text-sm border-b border-gray-100">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-900">{l.contractorName}</span>
                    <span className="font-semibold text-gray-900">${fmt(parseFloat(l.superAmount))}</span>
                  </div>
                  {members.length > 0 && l.id !== undefined && (
                    <div className="mt-1.5">
                      <select
                        value={memberId ?? ''}
                        onChange={(e) => onChoose(l.id!, e.target.value ? Number(e.target.value) : null)}
                        disabled={isFinalisedView}
                        className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-800"
                      >
                        <option value="">Select QuickSuper member</option>
                        {members.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name}{m.fundName ? ` - ${m.fundName}` : ''}
                          </option>
                        ))}
                      </select>
                      {memberId === null && (
                        <p className="mt-1 text-xs text-amber-700">QuickSuper member needed for the contribution file.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
          <div className="flex justify-between px-4 py-3 text-sm bg-gray-50 rounded-b-xl">
            <span className="font-semibold text-gray-700">QuickSuper total</span>
            <span className="font-bold text-brand-700">${fmt(total)}</span>
          </div>
        </div>
      )}

      {lines && (
        <div className="w-full mb-6 rounded-xl border border-gray-200 bg-white divide-y divide-gray-100 text-left text-sm">
          <div className="flex justify-between px-4 py-3">
            <span className="text-gray-500">Sent to Xero</span>
            <span className="font-medium text-gray-900">${fmt(xeroTotal)}</span>
          </div>
          <div className="flex justify-between px-4 py-3">
            <span className="text-gray-500">Paid via QuickSuper</span>
            <span className="font-medium text-gray-900">${fmt(total)}</span>
          </div>
          <div className="flex justify-between px-4 py-3">
            <span className="font-semibold text-gray-700">Total super for this run</span>
            <span className="font-bold text-brand-700">${fmt(xeroTotal + total)}</span>
          </div>
        </div>
      )}

      {lines && lines.length > 0 && (
        <div className="w-full mb-6 text-left">
          {members.length === 0 && (
            <p className="mb-2 text-xs text-gray-500">
              To build the QuickSuper file, go back and drop your QuickSuper Employees.csv into the import box first.
            </p>
          )}
          {members.length > 0 && !allMatched && (
            <p className="mb-2 text-xs text-amber-700">Choose a QuickSuper member for every contractor to enable the download.</p>
          )}
          {!periodValid && (
            <p className="mb-2 text-xs text-amber-700">The report period dates are missing, so the file can't be built.</p>
          )}
          {downloadError && (
            <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{downloadError}</div>
          )}
          <button
            onClick={handleDownload}
            disabled={!canDownload}
            className="btn-secondary w-full text-sm disabled:opacity-50"
          >
            {downloading ? 'Building file…' : 'Download QuickSuper contribution file (CSV)'}
          </button>
          <p className="mt-1 text-xs text-gray-400">Upload it in QuickSuper under Contribution Files, Upload File (CSV v2, date format DD-MMM-YY).</p>
        </div>
      )}

      {finaliseError && (
        <div className="w-full mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{finaliseError}</div>
      )}

      {isPreview ? null : finalised ? (
        <div className="w-full mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 text-center">
          Pay run finalised and archived. Find it under Past Runs.
        </div>
      ) : (
        <button
          onClick={handleFinalise}
          disabled={!lines || finalising}
          className="btn-primary w-full mb-3 text-base py-3 disabled:opacity-50"
        >
          {finalising ? 'Finalising…' : 'Finalise'}
        </button>
      )}
      {!isPreview && !finalised && (
        <p className="mb-3 text-xs text-gray-400 text-center">
          Only finalise once all super has been sent to Xero and paid in QuickSuper.
        </p>
      )}

      {!isPreview && finalised ? (
        <button onClick={onBack} className="btn-secondary w-full text-sm">Start New Import</button>
      ) : (
        <button onClick={onBack} className="btn-secondary w-full text-sm">Back</button>
      )}
    </div>
  );
}
